import type React from 'react';

/*
 * The wish story's clock. Every frame is a pure function of T — seconds
 * since the telling began — as in the design lab's composition this
 * ports ("Learning from Every Wish", 2026-09-22): the scenes run back to
 * back, each cue is the running sum of the durations before it, and the
 * whole telling loops.
 *
 * June, 2026-09-23: the closing mark and line are gone — the three levels
 * end it — and Jess's morning runs slower, 13s to 17s. Then more time to
 * read: each mom's words stay up until she walks off (Park 4s to 6s,
 * Return to 18s), and each engine is read large before it settles
 * (Learn 14s to 17s).
 */

export const SCENES = [
  { name: 'Arrive', dur: 6 }, //  Maya walks in with the kids and opens the app
  { name: 'Ask', dur: 7 }, //     she makes the wish; today's generic answer
  { name: 'Park', dur: 6 }, //    the park, again; the family trudges off
  { name: 'Night', dur: 4 }, //   her wish lifts out of the day
  { name: 'Learn', dur: 17 }, //  three engines attach to The Home CEO
  { name: 'Return', dur: 18 }, // Jess, the same wish, a specialised answer
  { name: 'Loop', dur: 10 }, //   the same wish at three levels
] as const;

export type SceneName = (typeof SCENES)[number]['name'];

export const CUES = SCENES.reduce(
  (acc, scene, i) => {
    acc.cues[scene.name] = acc.at;
    acc.at += SCENES[i].dur;
    return acc;
  },
  { cues: {} as Record<SceneName, number>, at: 0 },
).cues;

/** One telling, in seconds. */
export const TOTAL = SCENES.reduce((sum, scene) => sum + scene.dur, 0);

export type Ease = (t: number) => number;

export const ease = {
  linear: (t: number) => t,
  inQuad: (t: number) => t * t,
  outQuad: (t: number) => t * (2 - t),
  outCubic: (t: number) => 1 - (1 - t) ** 3,
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (2 - 2 * t) ** 3 / 2),
  outBack: (t: number) => 1 + 2.70158 * (t - 1) ** 3 + 1.70158 * (t - 1) ** 2,
} satisfies Record<string, Ease>;

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** 0 → 1 across [start, start + dur], eased: 0 before, 1 after. */
export const draw = (T: number, start: number, dur = 1, fn: Ease = ease.inOutCubic) => {
  if (T <= start) return 0;
  if (T >= start + dur) return 1;
  return fn((T - start) / dur);
};

/** from → to across [start, end], eased; the ends hold outside it. */
export const tween = (
  T: number,
  from: number,
  to: number,
  start: number,
  end: number,
  fn: Ease = ease.inOutCubic,
) => from + (to - from) * draw(T, start, end - start, fn);

/** Fade up into place. */
export const enter = (T: number, start: number, dur = 0.6, dist = 24): React.CSSProperties => {
  const p = draw(T, start, dur, ease.outCubic);
  return { opacity: p, transform: `translateY(${(1 - p) * dist}px)` };
};

/** Pop in with a little overshoot. */
export const pop = (T: number, start: number, dur = 0.5): React.CSSProperties => {
  const p = draw(T, start, dur, ease.outBack);
  return { opacity: clamp(p * 3, 0, 1), transform: `scale(${p})` };
};
